/**
 * React hooks for Funnel API
 */

import { useState, useCallback, useMemo } from "react";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useMutation, useQuery, useQueries } from "@tanstack/react-query";
import {
  getFunnelAnalysis,
  validateFunnelConfig,
  getFunnelSuggestions,
} from "@/lib/api/funnel-api";
import {
  blockFunnelAnalysis,
  FunnelAnalysisBlockedError,
  logFunnelEvent,
  reportFunnelFailure,
} from "@/lib/observability/funnels";
import { isDemoSite, demoGenerators } from "@/lib/utils/demoData";
import type {
  FunnelRequestParams,
  FunnelStep,
  FunnelResponse,
  FunnelSuggestionsResponse,
} from "@/lib/types/funnel-api";

/** A run is cross-domain when any step overrides the dashboard's property. */
const isCrossDomainRun = (params: FunnelRequestParams) =>
  params.steps.some((step) => Boolean((step as { property_id?: string }).property_id));

const TRANSIENT_FUNNEL_STATUS_CODES = new Set([408, 500, 502, 503, 504]);
const MAX_FUNNEL_RUN_RETRIES = 1;

const getStatusFromError = (error: unknown): number | null => {
  if (!error || typeof error !== "object") return null;

  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
  };

  const status = candidate.status ?? candidate.statusCode ?? candidate.response?.status;
  return typeof status === "number" ? status : null;
};

export const shouldRetryFunnelRun = (failureCount: number, error: unknown) => {
  if (failureCount >= MAX_FUNNEL_RUN_RETRIES) return false;
  if (error instanceof FunnelAnalysisBlockedError) return false;

  const status = getStatusFromError(error);
  if (status === null) return true;

  return TRANSIENT_FUNNEL_STATUS_CODES.has(status);
};

/**
 * Hook for fetching funnel analysis data
 * @param propertyId - Property ID to use for the analysis (from outlet context)
 * @param params - Funnel request parameters
 */
const useFunnelAnalysis = (
  propertyId: string | undefined,
  params: FunnelRequestParams | null
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["funnel-analysis", propertyId, params],
    queryFn: async () => {
      if (!token || !propertyId || !params) {
        throw new Error("Missing required parameters");
      }

      // Validate before sending
      const errors = validateFunnelConfig(params);
      if (errors.length > 0) {
        throw new Error(`Validation errors: ${errors.join(", ")}`);
      }

      return await getFunnelAnalysis(propertyId, params, token);
    },
    enabled: Boolean(token && propertyId && params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

/**
 * Hook for managing funnel configuration state
 */
const useFunnelConfig = () => {
  const [config, setConfig] = useState<FunnelRequestParams>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date().toISOString(),
    steps: [
      {
        name: "Step 1",
        conditions: [
          {
            type: "page_view",
            path: "/",
          },
        ],
      },
      {
        name: "Step 2",
        conditions: [
          {
            type: "page_view",
            path: "/pricing",
          },
        ],
      },
    ],
    mode: "sequential",
  });

  const updateConfig = useCallback((updates: Partial<FunnelRequestParams>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const addStep = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          name: `Step ${prev.steps.length + 1}`,
          conditions: [
            {
              type: "page_view",
              path: "",
            },
          ],
        },
      ],
    }));
  }, []);

  const removeStep = useCallback((index: number) => {
    setConfig((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  }, []);

  const updateStep = useCallback(
    (index: number, updates: Partial<(typeof config.steps)[0]>) => {
      setConfig((prev) => ({
        ...prev,
        steps: prev.steps.map((step, i) =>
          i === index ? { ...step, ...updates } : step
        ),
      }));
    },
    []
  );

  const validate = useCallback(() => {
    return validateFunnelConfig(config);
  }, [config]);

  return {
    config,
    setConfig,
    updateConfig,
    addStep,
    removeStep,
    updateStep,
    validate,
  };
};

/**
 * Hook for running funnel analysis with mutation
 * @param propertyId - Property ID to use for the analysis (from outlet context)
 */
export const useRunFunnelAnalysis = (propertyId?: string) => {
  const { token } = useAuth();

  return useMutation({
    mutationFn: async (params: FunnelRequestParams) => {
      if (!token || !propertyId) {
        throw blockFunnelAnalysis(
          "missing_token_or_property",
          "Missing API token or property ID",
          { propertyId },
          { stepCount: params.steps.length },
        );
      }

      // Demo site: return synthetic analysis without hitting the real API
      if (isDemoSite(propertyId)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return demoGenerators.funnelAnalysis(params.steps.map((s) => s.name));
      }

      const errors = validateFunnelConfig(params);
      if (errors.length > 0) {
        throw blockFunnelAnalysis(
          "invalid_config",
          `Validation errors: ${errors.join(", ")}`,
          { propertyId },
          { stepCount: params.steps.length, crossDomain: isCrossDomainRun(params) },
        );
      }

      return await getFunnelAnalysis(propertyId, params, token);
    },
    onSuccess: (_result, params) => {
      logFunnelEvent("analysis_run", { propertyId }, {
        stepCount: params.steps.length,
        crossDomain: isCrossDomainRun(params),
      });
    },
    onError: (error, params) => {
      reportFunnelFailure("run_analysis", error, { propertyId }, {
        stepCount: params.steps.length,
        crossDomain: isCrossDomainRun(params),
      });
    },
    retry: shouldRetryFunnelRun,
  });
};

/**
 * Hook for fetching funnel step suggestions
 * @param propertyId - Property ID to use for the analysis (from outlet context)
 * @param options - Optional date range parameters
 */
export const useFunnelSuggestions = (
  propertyId: string | undefined,
  options?: {
    start?: string;
    end?: string;
    search?: string;
    timezone?: string;
    granularity?: string;
  }
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["funnel-suggestions", propertyId, options],
    queryFn: async () => {
      if (!token || !propertyId) {
        throw new Error("Missing API token or property ID");
      }

      // Demo site: return synthetic suggestions without hitting the real API
      if (isDemoSite(propertyId)) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return demoGenerators.funnelSuggestions();
      }

      return await getFunnelSuggestions(propertyId, token, options);
    },
    enabled: Boolean(token && propertyId),
    staleTime: options?.search ? 60 * 1000 : 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * A single "how many sessions hit this target, any way" query.
 * key — stable identity (so unchanged steps don't refetch).
 */
export interface StepMatchQuery {
  key: string;
  propertyId: string;
  start: string;
  end: string;
  timezone?: string;
  granularity?: string;
  conditions: FunnelStep["conditions"];
}

interface StepMatchTotals {
  sessions: number;
  events: number;
}

/**
 * Exact per-step "matched total" — sessions and events that hit a step's target(s)
 * regardless of funnel path. Uses the funnel engine with mode=unbounded; step 0
 * returns the full matched count as total_entered / total_events.
 *
 * Returns a Map of query.key -> { sessions, events }.
 */
export const useStepMatchedTotals = (queries: StepMatchQuery[]): Map<string, StepMatchTotals> => {
  const { token } = useAuth();

  const results = useQueries({
    queries: queries.map((q) => ({
      queryKey: ["funnel-step-matched", q.propertyId, q.start, q.end, q.timezone, q.granularity, q.key],
      queryFn: async (): Promise<StepMatchTotals> => {
        if (!token || !q.propertyId || !q.conditions?.length) {
          throw new Error("Missing token / property / conditions");
        }

        // Demo site: return plausible match counts without hitting the real API
        if (isDemoSite(q.propertyId)) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          const demoResp = demoGenerators.funnelAnalysis();
          return {
            sessions: demoResp.summary?.total_entered ?? demoResp.data?.steps?.[0]?.total_sessions ?? 0,
            events: demoResp.data?.steps?.[0]?.total_events ?? 0,
          };
        }

        // Two identical steps keeps us clear of any "min 2 steps" rule; total_entered is
        // always the step-1 (= matched) count regardless of the second step.
        const params: FunnelRequestParams = {
          start: q.start,
          end: q.end,
          mode: "unbounded",
          timezone: q.timezone,
          granularity: q.granularity,
          steps: [
            { name: "match", conditions: q.conditions },
            { name: "match", conditions: q.conditions },
          ],
        };
        const resp = await getFunnelAnalysis(q.propertyId, params, token);
        return {
          sessions: resp.summary?.total_entered ?? resp.data?.steps?.[0]?.total_sessions ?? 0,
          events: resp.data?.steps?.[0]?.total_events ?? 0,
        };
      },
      enabled: Boolean(token && q.propertyId && q.conditions?.length),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, StepMatchTotals>();
    queries.forEach((q, i) => {
      const d = results[i]?.data;
      if (d) map.set(q.key, d);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, queries.map((q) => q.key).join("|")]);
};

/**
 * Hook for fetching funnel step suggestions for multiple properties in parallel.
 * Returns a Map of propertyId -> FunnelSuggestionsResponse.
 */
export const useMultiPropertySuggestions = (
  propertyIds: string[]
): Map<string, FunnelSuggestionsResponse> => {
  const { token } = useAuth();

  const results = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ["funnel-suggestions", propertyId],
      queryFn: async () => {
        if (!token) throw new Error("Missing token");
        if (isDemoSite(propertyId)) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return demoGenerators.funnelSuggestions();
        }
        return await getFunnelSuggestions(propertyId, token);
      },
      enabled: Boolean(token && propertyId),
      staleTime: 10 * 60 * 1000,
      retry: 1,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, FunnelSuggestionsResponse>();
    propertyIds.forEach((id, index) => {
      const data = results[index]?.data;
      if (data) map.set(id, data);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, propertyIds.join(",")]);
};
